/**
 * Price utilities shared across order/budget views.
 *
 * The API returns prices as strings using either an Italian decimal comma
 * (e.g. `"107,7"`) or a regular dot (`"107.7"`). `Number("107,7")` is `NaN`,
 * so any comma must be normalised before parsing.
 */

type PriceInput = string | number | null | undefined;

/**
 * Parse a price-like value into a finite number.
 *
 * - Normalises Italian comma decimals (`"107,7"` → `107.7`).
 * - Returns `0` for `null` / `undefined` / empty strings / non-finite values.
 */
export const parsePrice = (value: PriceInput): number => {
  if (value === null || value === undefined || value === '') return 0;
  const normalised = typeof value === 'string' ? value.replace(',', '.') : value;
  const parsed = Number(normalised);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Format a price-like value for display using the `it-IT` style
 * (`4342.65` → `"4.342,65"`): a dot as the thousands separator and a
 * comma as the decimal separator, with an optional currency prefix.
 *
 * Robust against:
 *  - missing / null values  → renders as `0,00`
 *  - comma-decimal strings  → parsed via {@link parsePrice}
 */
export const formatPrice = (currency: string | undefined, value: PriceInput): string => {
  const formatted = parsePrice(value).toLocaleString('it-IT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true,
  });
  return `${currency ?? ''} ${formatted}`.trim();
};

/**
 * Format a price for the order page using the `en-US` style
 * (`4342.65` → `"4,342.65"`): a comma as the thousands separator and a dot
 * before the cents, with an optional currency prefix. Same input handling as
 * {@link formatPrice}.
 */
export const formatOrderPrice = (currency: string | undefined, value: PriceInput): string => {
  const formatted = parsePrice(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true,
  });
  return `${currency ?? ''} ${formatted}`.trim();
};

/**
 * Format an integer count using the same `it-IT` grouping style as
 * {@link formatPrice} (`1234` → `"1.234"`); an em dash for null/undefined.
 */
export const formatCount = (value: number | null | undefined): string =>
  value === null || value === undefined ? '—' : value.toLocaleString('it-IT');
